import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { httpsCallable } from 'firebase/functions';
import { collection, query, where, onSnapshot } from 'firebase/firestore';
import {
  Box,
  VStack,
  HStack,
  Text,
  Button,
  IconButton,
  Tooltip,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Badge,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  MenuDivider,
  Divider,
  Spinner,
  Center,
  useToast,
  useColorModeValue,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  FormControl,
  FormLabel,
  FormHelperText,
  Input,
  Select,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  Textarea,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Code,
  Checkbox,
  CheckboxGroup,
  SimpleGrid,
  AlertDialog,
  AlertDialogOverlay,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogBody,
  AlertDialogFooter,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Grid,
  GridItem,
} from '@chakra-ui/react';
import {
  FiPlus,
  FiMoreVertical,
  FiRotateCw,
  FiEdit2,
  FiTrash2,
  FiCopy,
  FiPause,
  FiPlay,
  FiRefreshCw,
  FiCpu,
} from 'react-icons/fi';
import { functionsUsEast1, db } from '../../../utils/init-firebase';
import AgentConnectorPanel from './AgentConnectorPanel';

/**
 * Admin > API Keys > "AI Agent Keys" tab.
 *
 * Manages the per-user agent keys (Supabase crm_agent_keys) that authenticate
 * the Origin Agent API (agentApiHTTP) — the tool surface external AI agents
 * (ChatGPT Custom GPTs, MCP clients) use to act AS a specific loan officer.
 *
 * All data flows through the `manageAgentKeys` callable (us-east1). Keys are
 * stored as SHA-256 hashes; the full key value is shown exactly once at
 * create/rotate.
 */

const AGENT_API_BASE_URL = 'https://fhl.one/api/agent/v1';

const SCOPE_OPTIONS = [
  { id: 'read', label: 'Read', help: 'Contacts, deals, tasks, activity, campaigns' },
  { id: 'write_deals', label: 'Edit deals', help: 'Deal fields, stage changes, deal notes' },
  { id: 'write_contacts', label: 'Edit contacts', help: 'Contact fields, tags, contact notes' },
  { id: 'write_tasks', label: 'Manage tasks', help: 'Create, update, complete tasks' },
  { id: 'create_leads', label: 'Create leads', help: 'New contact + deal via the lead pipeline' },
  { id: 'send_sms', label: 'Send SMS', help: 'Text through the user\u2019s Dialpad line' },
  { id: 'send_email', label: 'Send email', help: 'Send as the user + create email drafts' },
  { id: 'manage_campaigns', label: 'Manage campaigns', help: 'Enroll, unenroll, pause campaigns' },
];
const ALL_SCOPE_IDS = SCOPE_OPTIONS.map((s) => s.id);

// Elevated scopes: never part of the default grant. The backend only accepts
// them from a SuperAdmin caller onto a key bound to a SuperAdmin user, and the
// MCP gateways re-check eligibility on every call. gam_admin is additionally
// restricted server-side to allowlisted uids (Mitchell).
const ELEVATED_SCOPE_OPTIONS = [
  {
    id: 'manage_agents',
    label: 'Manage Hub Agents',
    help: 'Agents Portal ops over MCP (create/configure agents, runs, logs, findings). SuperAdmin-bound keys only.',
  },
  {
    id: 'gam_admin',
    label: 'Google Workspace Admin (GAM)',
    help: 'Full Google Workspace admin via the GAM MCP (users, groups, Gmail, Drive, security audits). Allowlisted SuperAdmin uids only — the server rejects grants to anyone else.',
  },
];

function formatDateTime(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

export default function AgentKeysPanel() {
  const toast = useToast();

  // ---- theme values (all hooks before any early return) ----
  const cardBG = useColorModeValue('white', 'gray.800');
  const subText = useColorModeValue('gray.600', 'gray.400');
  const tableHeadBG = useColorModeValue('gray.50', 'gray.700');
  const keyBoxBG = useColorModeValue('gray.100', 'gray.700');

  // ---- data state ----
  const [keys, setKeys] = useState([]);
  const [crmUsers, setCrmUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  // ---- create/edit modal state ----
  const formModal = useDisclosure();
  const [formMode, setFormMode] = useState('create'); // 'create' | 'edit'
  const [formKeyId, setFormKeyId] = useState(null);
  const [formUserId, setFormUserId] = useState('');
  const [formLabel, setFormLabel] = useState('');
  const [formScopes, setFormScopes] = useState(ALL_SCOPE_IDS);
  const [formRateLimit, setFormRateLimit] = useState(300);
  const [formNotes, setFormNotes] = useState('');

  // ---- show-once modal state ----
  const showOnceModal = useDisclosure();
  const [newKey, setNewKey] = useState(null); // { value, label, userName, rotated }
  const [savedConfirmed, setSavedConfirmed] = useState(false);
  const [copied, setCopied] = useState(false);

  // ---- rotate dialog state ----
  const rotateDialog = useDisclosure();
  const [rotateTarget, setRotateTarget] = useState(null);
  const rotateCancelRef = useRef();

  // ---- delete dialog state ----
  const deleteDialog = useDisclosure();
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const deleteCancelRef = useRef();

  const manageAgentKeys = useMemo(() => httpsCallable(functionsUsEast1, 'manageAgentKeys'), []);

  const loadKeys = useCallback(async () => {
    try {
      const res = await manageAgentKeys({ action: 'list' });
      setKeys(res.data?.keys || []);
    } catch (err) {
      console.error('[AgentKeysPanel] list error:', err);
      toast({
        title: 'Failed to load agent keys',
        description: err.message,
        status: 'error',
        duration: 6000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  }, [manageAgentKeys, toast]);

  useEffect(() => {
    loadKeys();
  }, [loadKeys]);

  // CRM user roster for the "acts as" picker.
  useEffect(() => {
    const q = query(collection(db, 'users'), where('CRMUser', '==', true));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const users = [];
      snapshot.forEach((userDoc) => {
        const u = userDoc.data();
        users.push({
          uid: userDoc.id,
          name: u.Name || u.name || u.displayName || u.Email || u.email || userDoc.id,
          email: u.Email || u.email || '',
        });
      });
      users.sort((a, b) => a.name.localeCompare(b.name));
      setCrmUsers(users);
    });
    return () => unsubscribe();
  }, []);

  // ---- stats ----
  const stats = useMemo(() => {
    const active = keys.filter((k) => k.status === 'active').length;
    const totalRequests = keys.reduce((sum, k) => sum + (Number(k.usage_count) || 0), 0);
    const lastUsedTimes = keys.map((k) => (k.last_used_at ? new Date(k.last_used_at).getTime() : 0));
    const lastUsed = Math.max(0, ...lastUsedTimes);
    return {
      total: keys.length,
      active,
      totalRequests,
      lastUsed: lastUsed > 0 ? formatDateTime(new Date(lastUsed).toISOString()) : 'Never',
    };
  }, [keys]);

  // ---- create / edit ----
  const openCreate = () => {
    setFormMode('create');
    setFormKeyId(null);
    setFormUserId('');
    setFormLabel('');
    setFormScopes(ALL_SCOPE_IDS);
    setFormRateLimit(300);
    setFormNotes('');
    formModal.onOpen();
  };

  const openEdit = (key) => {
    setFormMode('edit');
    setFormKeyId(key.id);
    setFormUserId(key.user_id);
    setFormLabel(key.label || '');
    setFormScopes(Array.isArray(key.scopes) && key.scopes.length ? key.scopes : ALL_SCOPE_IDS);
    setFormRateLimit(key.rate_limit_per_hour || 300);
    setFormNotes(key.notes || '');
    formModal.onOpen();
  };

  const submitForm = async () => {
    const label = formLabel.trim();
    if (!label) {
      toast({ title: 'Label is required', status: 'warning', duration: 3000, isClosable: true });
      return;
    }
    if (formMode === 'create' && !formUserId) {
      toast({ title: 'Pick the user this key acts as', status: 'warning', duration: 3000, isClosable: true });
      return;
    }
    if (!formScopes.length) {
      toast({ title: 'Select at least one scope', status: 'warning', duration: 3000, isClosable: true });
      return;
    }
    setBusy(true);
    try {
      if (formMode === 'create') {
        const res = await manageAgentKeys({
          action: 'create',
          targetUserId: formUserId,
          label,
          scopes: formScopes,
          rateLimitPerHour: formRateLimit,
          notes: formNotes.trim() || null,
        });
        formModal.onClose();
        const user = crmUsers.find((u) => u.uid === formUserId);
        setNewKey({ value: res.data.fullKey, label, userName: user?.name || '', rotated: false });
        setSavedConfirmed(false);
        setCopied(false);
        showOnceModal.onOpen();
      } else {
        await manageAgentKeys({
          action: 'update',
          id: formKeyId,
          label,
          scopes: formScopes,
          rateLimitPerHour: formRateLimit,
          notes: formNotes.trim() || null,
        });
        formModal.onClose();
        toast({ title: 'Agent key updated', status: 'success', duration: 3000, isClosable: true });
      }
      await loadKeys();
    } catch (err) {
      console.error('[AgentKeysPanel] save error:', err);
      toast({ title: 'Save failed', description: err.message, status: 'error', duration: 6000, isClosable: true });
    } finally {
      setBusy(false);
    }
  };

  // ---- rotate ----
  const openRotate = (key) => {
    setRotateTarget(key);
    rotateDialog.onOpen();
  };

  const confirmRotate = async () => {
    if (!rotateTarget) return;
    setBusy(true);
    try {
      const res = await manageAgentKeys({ action: 'rotate', id: rotateTarget.id });
      rotateDialog.onClose();
      setNewKey({ value: res.data.fullKey, label: rotateTarget.label, userName: rotateTarget.user_name || '', rotated: true });
      setSavedConfirmed(false);
      setCopied(false);
      showOnceModal.onOpen();
      await loadKeys();
    } catch (err) {
      console.error('[AgentKeysPanel] rotate error:', err);
      toast({ title: 'Rotate failed', description: err.message, status: 'error', duration: 6000, isClosable: true });
    } finally {
      setBusy(false);
      setRotateTarget(null);
    }
  };

  // ---- enable / disable ----
  const toggleStatus = async (key) => {
    const nextStatus = key.status === 'active' ? 'disabled' : 'active';
    setBusy(true);
    try {
      await manageAgentKeys({ action: 'update', id: key.id, status: nextStatus });
      toast({
        title: nextStatus === 'disabled' ? `"${key.label}" disabled` : `"${key.label}" enabled`,
        description: nextStatus === 'disabled'
          ? 'The AI agent using this key now receives 403 Invalid API key.'
          : 'This key can authenticate requests again.',
        status: nextStatus === 'disabled' ? 'warning' : 'success',
        duration: 4000,
        isClosable: true,
      });
      await loadKeys();
    } catch (err) {
      console.error('[AgentKeysPanel] status toggle error:', err);
      toast({ title: 'Status change failed', description: err.message, status: 'error', duration: 6000, isClosable: true });
    } finally {
      setBusy(false);
    }
  };

  // ---- delete ----
  const openDelete = (key) => {
    setDeleteTarget(key);
    setDeleteConfirmText('');
    deleteDialog.onOpen();
  };

  const confirmDelete = async () => {
    if (!deleteTarget || deleteConfirmText !== deleteTarget.label) return;
    setBusy(true);
    try {
      await manageAgentKeys({ action: 'delete', id: deleteTarget.id });
      deleteDialog.onClose();
      toast({ title: `"${deleteTarget.label}" deleted`, status: 'info', duration: 4000, isClosable: true });
      await loadKeys();
    } catch (err) {
      console.error('[AgentKeysPanel] delete error:', err);
      toast({ title: 'Delete failed', description: err.message, status: 'error', duration: 6000, isClosable: true });
    } finally {
      setBusy(false);
      setDeleteTarget(null);
    }
  };

  // ---- show-once helpers ----
  const copyNewKey = async () => {
    try {
      await navigator.clipboard.writeText(newKey?.value || '');
      setCopied(true);
      toast({ title: 'Key copied to clipboard', status: 'success', duration: 2000, isClosable: true });
    } catch (err) {
      toast({ title: 'Copy failed — select and copy manually', status: 'warning', duration: 4000, isClosable: true });
    }
  };

  const closeShowOnce = () => {
    showOnceModal.onClose();
    setNewKey(null);
    setSavedConfirmed(false);
    setCopied(false);
  };

  const scopeSummary = (key) => {
    const scopes = Array.isArray(key.scopes) ? key.scopes : [];
    const standard = scopes.filter((s) => ALL_SCOPE_IDS.includes(s));
    let suffix = '';
    if (scopes.includes('manage_agents')) suffix += ' + Agents';
    if (scopes.includes('gam_admin')) suffix += ' + GAM';
    if (standard.length >= ALL_SCOPE_IDS.length) return `Full access${suffix}`;
    return `${standard.length} of ${ALL_SCOPE_IDS.length}${suffix}`;
  };

  if (loading) {
    return (
      <Center py={16} flexDir="column">
        <Spinner size="xl" color="purple.500" thickness="4px" mb={4} />
        <Text color={subText}>Loading agent keys...</Text>
      </Center>
    );
  }

  return (
    <VStack align="stretch" spacing={6}>
      {/* Stats + actions */}
      <Box bg={cardBG} p={6} borderRadius="xl" shadow="sm">
        <HStack justify="space-between" align="flex-start" mb={4}>
          <VStack align="flex-start" spacing={1}>
            <HStack>
              <FiCpu />
              <Text fontWeight="bold" fontSize="lg">AI Agent Keys</Text>
            </HStack>
            <Text color={subText} fontSize="sm">
              Each key is bound to one loan officer. An AI agent holding the key acts as that user —
              same deals, same contacts, same sending identity.
            </Text>
          </VStack>
          <HStack>
            <Tooltip label="Refresh">
              <IconButton icon={<FiRefreshCw />} aria-label="Refresh agent keys" variant="ghost" onClick={loadKeys} isDisabled={busy} />
            </Tooltip>
            <Button leftIcon={<FiPlus />} colorScheme="purple" onClick={openCreate} isDisabled={busy}>
              Create Agent Key
            </Button>
          </HStack>
        </HStack>
        <Grid templateColumns="repeat(4, 1fr)" gap={4}>
          <GridItem>
            <Stat>
              <StatLabel fontSize="xs">Total Keys</StatLabel>
              <StatNumber>{stats.total}</StatNumber>
            </Stat>
          </GridItem>
          <GridItem>
            <Stat>
              <StatLabel fontSize="xs">Active Keys</StatLabel>
              <StatNumber color={stats.active > 0 ? 'green.500' : 'orange.500'}>{stats.active}</StatNumber>
              <StatHelpText>{stats.total - stats.active} disabled</StatHelpText>
            </Stat>
          </GridItem>
          <GridItem>
            <Stat>
              <StatLabel fontSize="xs">Total Requests</StatLabel>
              <StatNumber>{stats.totalRequests.toLocaleString()}</StatNumber>
              <StatHelpText>all-time, all keys</StatHelpText>
            </Stat>
          </GridItem>
          <GridItem>
            <Stat>
              <StatLabel fontSize="xs">Last Request</StatLabel>
              <StatNumber fontSize="md">{stats.lastUsed}</StatNumber>
            </Stat>
          </GridItem>
        </Grid>
      </Box>

      {/* Keys table */}
      <Box bg={cardBG} borderRadius="xl" shadow="sm" overflow="hidden">
        <TableContainer>
          <Table variant="simple" size="sm">
            <Thead bg={tableHeadBG}>
              <Tr>
                <Th>Label</Th>
                <Th>Acts As</Th>
                <Th>Key</Th>
                <Th>Status</Th>
                <Th>Scopes</Th>
                <Th isNumeric>Limit / hr</Th>
                <Th>Created</Th>
                <Th>Last Used</Th>
                <Th isNumeric>Requests</Th>
                <Th width="1%" />
              </Tr>
            </Thead>
            <Tbody>
              {keys.length === 0 && (
                <Tr>
                  <Td colSpan={10}>
                    <Center py={10} flexDir="column">
                      <Text color={subText} mb={3}>No agent keys yet.</Text>
                      <Button size="sm" leftIcon={<FiPlus />} colorScheme="purple" onClick={openCreate}>
                        Create your first agent key
                      </Button>
                    </Center>
                  </Td>
                </Tr>
              )}
              {keys.map((key) => (
                <Tr key={key.id}>
                  <Td>
                    <VStack align="flex-start" spacing={0}>
                      <Text fontWeight="semibold">{key.label}</Text>
                      {key.notes && (
                        <Text fontSize="xs" color={subText} noOfLines={1} maxW="220px">
                          {key.notes}
                        </Text>
                      )}
                    </VStack>
                  </Td>
                  <Td>
                    <VStack align="flex-start" spacing={0}>
                      <Text fontSize="sm" fontWeight="medium">{key.user_name || key.user_id}</Text>
                      {key.user_email && (
                        <Text fontSize="xs" color={subText}>{key.user_email}</Text>
                      )}
                    </VStack>
                  </Td>
                  <Td>
                    <Tooltip label="The full key is only shown once, at creation or rotation">
                      <Code fontSize="xs">{key.key_prefix}…</Code>
                    </Tooltip>
                  </Td>
                  <Td>
                    <Badge colorScheme={key.status === 'active' ? 'green' : 'red'}>{key.status}</Badge>
                  </Td>
                  <Td>
                    <Tooltip
                      label={(Array.isArray(key.scopes) ? key.scopes : []).join(', ') || 'none'}
                      maxW="320px"
                    >
                      <Badge colorScheme={scopeSummary(key).startsWith('Full access') ? 'purple' : 'gray'}>
                        {scopeSummary(key)}
                      </Badge>
                    </Tooltip>
                  </Td>
                  <Td isNumeric>{key.rate_limit_per_hour}</Td>
                  <Td>
                    <VStack align="flex-start" spacing={0}>
                      <Text fontSize="xs">{formatDateTime(key.created_at)}</Text>
                      {key.created_by_name && (
                        <Text fontSize="xs" color={subText}>by {key.created_by_name}</Text>
                      )}
                    </VStack>
                  </Td>
                  <Td>
                    <Text fontSize="xs">{formatDateTime(key.last_used_at)}</Text>
                  </Td>
                  <Td isNumeric>{Number(key.usage_count || 0).toLocaleString()}</Td>
                  <Td>
                    <Menu placement="bottom-end">
                      <MenuButton
                        as={IconButton}
                        icon={<FiMoreVertical />}
                        aria-label={`Actions for ${key.label}`}
                        variant="ghost"
                        size="sm"
                        isDisabled={busy}
                      />
                      <MenuList>
                        <MenuItem icon={<FiRotateCw />} onClick={() => openRotate(key)}>
                          Rotate key
                        </MenuItem>
                        <MenuItem icon={<FiEdit2 />} onClick={() => openEdit(key)}>
                          Edit
                        </MenuItem>
                        <MenuItem
                          icon={key.status === 'active' ? <FiPause /> : <FiPlay />}
                          onClick={() => toggleStatus(key)}
                        >
                          {key.status === 'active' ? 'Disable' : 'Enable'}
                        </MenuItem>
                        <MenuDivider />
                        <MenuItem icon={<FiTrash2 />} color="red.500" onClick={() => openDelete(key)}>
                          Delete
                        </MenuItem>
                      </MenuList>
                    </Menu>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </TableContainer>
      </Box>

      {/* Endpoint info */}
      <Alert status="info" variant="left-accent" borderRadius="lg" alignItems="flex-start">
        <AlertIcon />
        <Box>
          <AlertTitle fontSize="sm">These keys authenticate the Origin Agent API</AlertTitle>
          <AlertDescription fontSize="sm">
            <VStack align="flex-start" spacing={1} mt={2}>
              <HStack spacing={2}>
                <Badge>Base URL</Badge>
                <Code fontSize="xs">{AGENT_API_BASE_URL}</Code>
              </HStack>
              <HStack spacing={2}>
                <Badge>OpenAPI</Badge>
                <Code fontSize="xs">{AGENT_API_BASE_URL}/openapi.json</Code>
              </HStack>
              <Text pt={1}>
                Agents send the key via the <Code fontSize="xs">X-API-Key</Code> header (or{' '}
                <Code fontSize="xs">Authorization: Bearer</Code>). Setup instructions for ChatGPT:{' '}
                <Code fontSize="xs">docs/chatgpt-agent-setup-guide.md</Code>. Full API reference:{' '}
                <Code fontSize="xs">docs/AGENT_API.md</Code>. Every request is audited in{' '}
                <Code fontSize="xs">crm_agent_actions</Code>.
              </Text>
            </VStack>
          </AlertDescription>
        </Box>
      </Alert>

      {/* ChatGPT OAuth connector (per-user Google-SSO linking + connections) */}
      <AgentConnectorPanel crmUsers={crmUsers} />

      {/* Create / Edit modal */}
      <Modal isOpen={formModal.isOpen} onClose={formModal.onClose} isCentered size="lg">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{formMode === 'create' ? 'Create Agent Key' : `Edit "${formLabel}"`}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4}>
              {formMode === 'create' ? (
                <FormControl isRequired>
                  <FormLabel>Acts as (loan officer)</FormLabel>
                  <Select
                    placeholder="Select a CRM user…"
                    value={formUserId}
                    onChange={(e) => setFormUserId(e.target.value)}
                  >
                    {crmUsers.map((u) => (
                      <option key={u.uid} value={u.uid}>
                        {u.name}{u.email ? ` — ${u.email}` : ''}
                      </option>
                    ))}
                  </Select>
                  <FormHelperText>
                    The AI agent will read and write this user's CRM data and send as them. Cannot be changed later.
                  </FormHelperText>
                </FormControl>
              ) : (
                <FormControl isDisabled>
                  <FormLabel>Acts as</FormLabel>
                  <Input value={crmUsers.find((u) => u.uid === formUserId)?.name || formUserId} isReadOnly />
                </FormControl>
              )}
              <FormControl isRequired>
                <FormLabel>Label</FormLabel>
                <Input
                  placeholder="e.g. Mitchell's ChatGPT"
                  value={formLabel}
                  onChange={(e) => setFormLabel(e.target.value)}
                />
                <FormHelperText>Which agent uses this key — e.g. "Jane's ChatGPT assistant".</FormHelperText>
              </FormControl>
              <FormControl>
                <FormLabel>Scopes</FormLabel>
                <CheckboxGroup value={formScopes} onChange={(vals) => setFormScopes(vals)}>
                  <SimpleGrid columns={2} spacing={2}>
                    {SCOPE_OPTIONS.map((scope) => (
                      <Tooltip key={scope.id} label={scope.help} placement="top" openDelay={300}>
                        <Box>
                          <Checkbox value={scope.id} size="sm">
                            {scope.label}
                          </Checkbox>
                        </Box>
                      </Tooltip>
                    ))}
                  </SimpleGrid>
                  <Divider my={3} />
                  <Text fontSize="xs" fontWeight="semibold" color={subText} mb={1}>
                    Elevated (SuperAdmin-bound keys only — never granted by default)
                  </Text>
                  <SimpleGrid columns={2} spacing={2}>
                    {ELEVATED_SCOPE_OPTIONS.map((scope) => (
                      <Tooltip key={scope.id} label={scope.help} placement="top" openDelay={300}>
                        <Box>
                          <Checkbox value={scope.id} size="sm" colorScheme="purple">
                            {scope.label}
                          </Checkbox>
                        </Box>
                      </Tooltip>
                    ))}
                  </SimpleGrid>
                </CheckboxGroup>
                <FormHelperText>Default is full standard access. Uncheck to restrict what the agent may do.</FormHelperText>
              </FormControl>
              <FormControl>
                <FormLabel>Rate limit (requests per hour)</FormLabel>
                <NumberInput
                  min={1}
                  max={100000}
                  value={formRateLimit}
                  onChange={(_, num) => setFormRateLimit(isNaN(num) ? 300 : num)}
                >
                  <NumberInputField />
                  <NumberInputStepper>
                    <NumberIncrementStepper />
                    <NumberDecrementStepper />
                  </NumberInputStepper>
                </NumberInput>
                <FormHelperText>Default 300. Agent conversations make several calls per question.</FormHelperText>
              </FormControl>
              <FormControl>
                <FormLabel>Notes (internal)</FormLabel>
                <Textarea
                  placeholder="Optional — which GPT/agent this powers, setup date, etc."
                  value={formNotes}
                  onChange={(e) => setFormNotes(e.target.value)}
                  rows={2}
                />
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={formModal.onClose} isDisabled={busy}>
              Cancel
            </Button>
            <Button colorScheme="purple" onClick={submitForm} isLoading={busy}>
              {formMode === 'create' ? 'Generate Key' : 'Save Changes'}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Show-once modal */}
      <Modal
        isOpen={showOnceModal.isOpen}
        onClose={closeShowOnce}
        isCentered
        closeOnOverlayClick={false}
        closeOnEsc={false}
        size="lg"
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            {newKey?.rotated ? `New secret for "${newKey?.label}"` : `Agent key for "${newKey?.label}"`}
          </ModalHeader>
          <ModalBody>
            <VStack spacing={4} align="stretch">
              <Alert status="warning" borderRadius="md">
                <AlertIcon />
                <Box fontSize="sm">
                  <AlertTitle>This key is shown only once.</AlertTitle>
                  <AlertDescription>
                    Copy it now and paste it into the AI agent's auth settings
                    {newKey?.userName ? ` (acts as ${newKey.userName})` : ''}. It cannot be recovered — only rotated.
                    {newKey?.rotated && ' The previous key value has already stopped working.'}
                  </AlertDescription>
                </Box>
              </Alert>
              <HStack bg={keyBoxBG} p={3} borderRadius="md" justify="space-between">
                <Text fontFamily="mono" fontSize="sm" wordBreak="break-all" userSelect="all">
                  {newKey?.value}
                </Text>
                <Tooltip label={copied ? 'Copied!' : 'Copy to clipboard'}>
                  <IconButton
                    icon={<FiCopy />}
                    aria-label="Copy key"
                    size="sm"
                    colorScheme={copied ? 'green' : 'purple'}
                    onClick={copyNewKey}
                  />
                </Tooltip>
              </HStack>
              <Text fontSize="sm" color={subText}>
                In ChatGPT: GPT Editor → Actions → Authentication → <b>API Key</b> → Auth Type <b>Custom</b> →
                header name <Code fontSize="xs">X-API-Key</Code> → paste this key.
              </Text>
              <Checkbox isChecked={savedConfirmed} onChange={(e) => setSavedConfirmed(e.target.checked)}>
                I have copied this key and stored it somewhere safe
              </Checkbox>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button colorScheme="purple" onClick={closeShowOnce} isDisabled={!savedConfirmed}>
              Done
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Rotate confirm dialog */}
      <AlertDialog
        isOpen={rotateDialog.isOpen}
        leastDestructiveRef={rotateCancelRef}
        onClose={rotateDialog.onClose}
        isCentered
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader>Rotate "{rotateTarget?.label}"?</AlertDialogHeader>
            <AlertDialogBody>
              <VStack align="stretch" spacing={3} fontSize="sm">
                <Text>
                  A new secret is generated for this key and the current value{' '}
                  <Text as="span" fontWeight="bold">stops working immediately</Text>. The AI agent must be updated with
                  the new key before it can make requests again.
                </Text>
              </VStack>
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={rotateCancelRef} variant="ghost" onClick={rotateDialog.onClose} isDisabled={busy}>
                Cancel
              </Button>
              <Button colorScheme="orange" onClick={confirmRotate} ml={3} isLoading={busy} leftIcon={<FiRotateCw />}>
                Rotate Key
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>

      {/* Delete confirm dialog */}
      <AlertDialog
        isOpen={deleteDialog.isOpen}
        leastDestructiveRef={deleteCancelRef}
        onClose={deleteDialog.onClose}
        isCentered
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader>Delete "{deleteTarget?.label}"?</AlertDialogHeader>
            <AlertDialogBody>
              <VStack align="stretch" spacing={3} fontSize="sm">
                <Text>
                  This permanently deletes the key. The AI agent using it will start receiving{' '}
                  <Code fontSize="xs">403 Invalid API key</Code>. This cannot be undone.
                </Text>
                <FormControl>
                  <FormLabel fontSize="sm">
                    Type <Text as="span" fontWeight="bold">{deleteTarget?.label}</Text> to confirm
                  </FormLabel>
                  <Input
                    value={deleteConfirmText}
                    onChange={(e) => setDeleteConfirmText(e.target.value)}
                    placeholder={deleteTarget?.label}
                  />
                </FormControl>
              </VStack>
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={deleteCancelRef} variant="ghost" onClick={deleteDialog.onClose} isDisabled={busy}>
                Cancel
              </Button>
              <Button
                colorScheme="red"
                onClick={confirmDelete}
                ml={3}
                isLoading={busy}
                isDisabled={!deleteTarget || deleteConfirmText !== deleteTarget.label}
                leftIcon={<FiTrash2 />}
              >
                Delete Key
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </VStack>
  );
}
